import { useAuth0 } from "@auth0/auth0-react";
import { get } from "lodash";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { AgentType, extractParamsWithPrefix } from "@superblocksteam/shared";
import type { Agent } from "@superblocksteam/shared";

import { forgetTokenVaultSeedAttempt } from "core/auth/tokenVaultSeed";
import { useIsControlFlowEnabled } from "hooks/api/useIsControlFlowEnabled";
import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { getAppProfilesInCurrentMode } from "legacy/selectors/applicationSelectors";
import { getQueryParams } from "legacy/utils/Utils";
import { clearCopiedBlocksFromLocalStorage } from "pages/Editors/ApiEditor/ControlFlow/hooks/useCopyPasteBlocks";
import { selectActiveAgents } from "store/slices/agents";
import { logoutApisV3 } from "store/slices/apisShared/client-auth";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import logger from "utils/logger";

export const LogoutComponent = () => {
  const { logout } = useAuth0();

  const organization = useSelector(selectOnlyOrganization);
  const profiles = useSelector(getAppProfilesInCurrentMode);
  const profile = profiles?.selected;

  const agents: Agent[] = useSelector(
    selectActiveAgents(
      organization?.agentType ?? AgentType.MULTITENANT,
      profile?.key ?? "",
    ),
  );

  const isControlFlow = useIsControlFlowEnabled();
  const enableDataPlaneGateway = useFeatureFlag(Flag.ENABLE_DATA_PLANE_GATEWAY);
  // Clear all agent cookies.
  useEffect(() => {
    (async () => {
      try {
        if (!organization || !agents) {
          throw new Error("no org or agent found");
        }
        clearCopiedBlocksFromLocalStorage();
        if (isControlFlow) {
          await logoutApisV3({
            orchestrator: true,
            agents,
            organization,
            enableDataPlaneGateway,
            profileKey: profile?.key,
          });
        }
        // logout of controller agents even if control flow is ON
        // just in case the flag was turned on recently and the agent is still authed
        await logoutApisV3({
          orchestrator: false,
          agents,
          organization,
        }).catch((err) => {
          logger.warn(
            `Failed to clear agent cookies ${err} (possibly old agent version)`,
          );
        });
      } catch (err) {
        logger.info(`failed to clear cookies: ${err}`);
      } finally {
        forgetTokenVaultSeedAttempt();
        logout({ returnTo: window.location.origin });
      }
    })();
  });

  return <></>;
};

export const LoginComponent = () => {
  const { loginWithRedirect } = useAuth0();
  const redirectInitiated = useRef(false);

  useEffect(() => {
    if (redirectInitiated.current) return;
    redirectInitiated.current = true;

    const params = getQueryParams();
    const chatPrompt = params["chat_prompt"];
    const returnUrl = chatPrompt
      ? `/create?chat_prompt=${encodeURIComponent(chatPrompt)}`
      : undefined;

    loginWithRedirect({
      appState: {
        ...(returnUrl ? { returnUrl } : {}),
      },
    }).catch((err) => {
      logger.error(`Login redirect failed: ${err}`);
      redirectInitiated.current = false;
    });
  }, [loginWithRedirect]);

  return null;
};

export const SignupComponent = () => {
  const { loginWithRedirect } = useAuth0();
  const redirectInitiated = useRef(false);

  useEffect(() => {
    if (redirectInitiated.current) return;
    redirectInitiated.current = true;

    const params = getQueryParams();
    const utmParams = extractParamsWithPrefix(params, "utm_");
    const sbTrackingParams = extractParamsWithPrefix(params, "sbt_");
    const chatPrompt = params["chat_prompt"];
    const returnUrl = chatPrompt
      ? `/create?chat_prompt=${encodeURIComponent(chatPrompt)}`
      : undefined;

    let emailHint = "";
    try {
      emailHint = decodeURIComponent(get(params, "email") ?? "");
    } catch {
      emailHint = get(params, "email") ?? "";
    }

    loginWithRedirect({
      screen_hint: "signup",
      login_hint: emailHint,
      appState: {
        referrer: get(params, "referrer") ?? "",
        ...utmParams,
        ...sbTrackingParams,
        ...(returnUrl ? { returnUrl } : {}),
      },
    }).catch((err) => {
      logger.error(`Signup redirect failed: ${err}`);
      redirectInitiated.current = false;
    });
  }, [loginWithRedirect]);

  return null;
};
